import {useSyncExternalStore} from 'use-sync-external-store/shim';

import {safeLocalStorage} from '../util/localStorage';
import type {Thread} from './store/thread';

const STORAGE_KEY = 'wb-agent-thread-read-status';

export interface ReadStatusSnapshot {
  clientBootstrappedAt: number;
  lastViewed: Record<string, number>;
  // Most recent thread to settle while the chat was
  // closed and the user hasn't reopened since
  unseenSettledThread: UnseenSettledThread | null;
}

export interface UnseenSettledThread {
  threadId: string;
}

let cachedSnapshot: ReadStatusSnapshot = readInitialSnapshot();
const listeners = new Set<() => void>();
const readObservers = new Map<string, Set<() => boolean>>();

export function getReadStatusSnapshot(): ReadStatusSnapshot {
  return cachedSnapshot;
}

export function subscribeReadStatus(notify: () => void): () => void {
  listeners.add(notify);
  return () => {
    listeners.delete(notify);
  };
}

export function markThreadRead(threadId: string, now = Date.now()): void {
  const existing = cachedSnapshot.lastViewed[threadId];
  if (existing != null && existing >= now) {
    return;
  }

  commitSnapshot({
    ...cachedSnapshot,
    // TODO: Clean up stale entries (e.g. have useThreadReadTracker drop entries with no
    // newer thread activity when summaries refresh)
    lastViewed: {
      ...cachedSnapshot.lastViewed,
      [threadId]: now,
    },
  });
}

/** Marks a thread read and clears only its own completion indicator. */
export function markThreadReadAndClearUnseen(
  threadId: string,
  now = Date.now()
): void {
  const existing = cachedSnapshot.lastViewed[threadId];
  const shouldMarkRead = existing == null || existing < now;
  const shouldClearUnseen =
    cachedSnapshot.unseenSettledThread?.threadId === threadId;
  if (!shouldMarkRead && !shouldClearUnseen) {
    return;
  }

  commitSnapshot({
    ...cachedSnapshot,
    lastViewed: shouldMarkRead
      ? {...cachedSnapshot.lastViewed, [threadId]: now}
      : cachedSnapshot.lastViewed,
    unseenSettledThread: shouldClearUnseen
      ? null
      : cachedSnapshot.unseenSettledThread,
  });
}

/** Registers a mounted surface that visibly presents this thread's result. */
export function observeThreadAsRead(
  threadId: string,
  isObserved: () => boolean = () => true
): () => void {
  const observers = readObservers.get(threadId) ?? new Set();
  observers.add(isObserved);
  readObservers.set(threadId, observers);
  if (isObserved()) {
    markThreadReadAndClearUnseen(threadId);
  }

  let active = true;
  return () => {
    if (!active) {
      return;
    }
    active = false;
    const currentObservers = readObservers.get(threadId);
    currentObservers?.delete(isObserved);
    if (currentObservers?.size === 0) {
      readObservers.delete(threadId);
    }
  };
}

export function isThreadObservedAsRead(threadId: string): boolean {
  const observers = readObservers.get(threadId);
  return (
    observers != null && Array.from(observers).some(isObserved => isObserved())
  );
}

/** Marks a settled thread read when its result is already visible elsewhere. */
export function markObservedThreadSettled(
  thread: Pick<Thread, 'id' | 'lastActivityAt'>
): boolean {
  if (!isThreadObservedAsRead(thread.id)) {
    return false;
  }
  markThreadReadAndClearUnseen(
    thread.id,
    Math.max(Date.now(), thread.lastActivityAt)
  );
  return true;
}

export function isThreadUnread(
  thread: Pick<Thread, 'id' | 'status' | 'lastActivityAt'>,
  snapshot: ReadStatusSnapshot = cachedSnapshot
): boolean {
  if (thread.status === 'running') {
    return false;
  }
  // Including clientBootstrappedAt makes a post-cache-clear reload show zero unread threads.
  const floor = Math.max(
    snapshot.lastViewed[thread.id] ?? 0,
    snapshot.clientBootstrappedAt
  );
  // Compare against lastActivityAt (last user/agent message) — not updatedAt —
  // so a metadata-only update like a title rename can't surface the unread dot.
  return thread.lastActivityAt > floor;
}

/**
 * useThreadReadStatus subscribes the calling component to per-thread
 * read-status. The returned `isUnread` closes over the current snapshot;
 * `markRead` is referentially stable.
 */
export function useThreadReadStatus(): {
  isUnread: (
    thread: Pick<Thread, 'id' | 'status' | 'lastActivityAt'>
  ) => boolean;
  markRead: (threadId: string) => void;
} {
  const snapshot = useSyncExternalStore(
    subscribeReadStatus,
    getReadStatusSnapshot
  );
  return {
    isUnread: thread => isThreadUnread(thread, snapshot),
    markRead: markThreadRead,
  };
}

function readInitialSnapshot(): ReadStatusSnapshot {
  const parsed = safeLocalStorage.isAvailable
    ? parsePersisted(safeLocalStorage.getItem(STORAGE_KEY))
    : null;
  if (parsed != null) {
    return parsed;
  }

  // First sighting on this client (or storage disabled): stamp bootstrap
  // time and best-effort persist. When storage is off, marks still gate
  // dots for the rest of this session via the in-memory snapshot — they
  // just don't survive a reload.
  const fresh: ReadStatusSnapshot = {
    clientBootstrappedAt: Date.now(),
    lastViewed: {},
    unseenSettledThread: null,
  };
  writePersisted(fresh);
  return fresh;
}

function commitSnapshot(next: ReadStatusSnapshot): void {
  writePersisted(next);
  cachedSnapshot = next;
  notifyListeners();
}

function writePersisted(value: ReadStatusSnapshot): void {
  if (!safeLocalStorage.isAvailable) {
    return;
  }
  safeLocalStorage.setItem(STORAGE_KEY, JSON.stringify(value));
}

function parsePersisted(raw: string | null): ReadStatusSnapshot | null {
  if (raw == null) {
    return null;
  }
  // Handle malformed JSON
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed == null ||
      typeof parsed !== 'object' ||
      typeof (parsed as ReadStatusSnapshot).clientBootstrappedAt !== 'number'
    ) {
      return null;
    }
    const candidate = parsed as ReadStatusSnapshot;

    const lastViewed: Record<string, number> = {};
    if (
      candidate.lastViewed != null &&
      typeof candidate.lastViewed === 'object'
    ) {
      for (const [k, v] of Object.entries(candidate.lastViewed)) {
        if (typeof v === 'number' && Number.isFinite(v)) {
          // lastViewed maps every thread the user has opened
          lastViewed[k] = v;
        }
      }
    }
    return {
      clientBootstrappedAt: candidate.clientBootstrappedAt,
      lastViewed,
      unseenSettledThread: parseUnseenSettledThread(
        candidate.unseenSettledThread
      ),
    };
  } catch {
    return null;
  }
}

function notifyListeners(): void {
  for (const listener of Array.from(listeners)) {
    listener();
  }
}

export function recordUnseenSettledThread(thread: UnseenSettledThread): void {
  commitSnapshot({...cachedSnapshot, unseenSettledThread: thread});
}

/** Clears the unseen settled thread. Called when the user opens the chat. */
export function clearUnseenSettledThread(threadId?: string): void {
  if (
    cachedSnapshot.unseenSettledThread == null ||
    (threadId != null &&
      cachedSnapshot.unseenSettledThread.threadId !== threadId)
  ) {
    return;
  }
  commitSnapshot({...cachedSnapshot, unseenSettledThread: null});
}

function parseUnseenSettledThread(value: unknown): UnseenSettledThread | null {
  if (value == null || typeof value !== 'object') {
    return null;
  }
  const candidate = value as Partial<UnseenSettledThread> & {
    rootTurnId?: string;
  };
  const threadId = candidate.threadId ?? candidate.rootTurnId;
  if (typeof threadId === 'string') {
    return {threadId};
  }
  return null;
}

// Handles cross-tab sync. Another tab's write fires a `storage` event here. Re-parse
// the new value, replace the cached snapshot reference, and notify so
// consumers re-render. `storage` doesn't fire in the writing tab, so
// same-tab notifications go through `commitSnapshot` above.
window.addEventListener('storage', event => {
  if (event.key !== STORAGE_KEY) {
    return;
  }
  const parsed = parsePersisted(event.newValue);
  if (parsed == null) {
    return;
  }
  cachedSnapshot = parsed;
  notifyListeners();
});
